import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, Upload, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';

// PRESCRIPTION OCR AUTOFILL (Phase 4, #15 — pharma pack). The pharmacist
// photographs / uploads the paper prescription; the media pipeline OCRs it and
// the LLM shapes it into medicine lines. The lines are a READING AID for the
// pharmacist to verify against the paper — nothing is dispensed from here.

const POLL_MS = 2500;
const POLL_LIMIT = 60; // ~2.5 minutes, then the pharmacist retries

const PrescriptionOcrPanel = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!open) { setJob(null); setError(''); }
  }, [open]);

  // Poll the queued job until the pipeline lands its verdict.
  useEffect(() => {
    if (!job || ['done', 'failed'].includes(job.status)) return undefined;
    const id = setInterval(async () => {
      pollCount.current += 1;
      if (pollCount.current > POLL_LIMIT) {
        clearInterval(id);
        setError(t('aiPacks.timeout', 'This is taking longer than expected — try again.'));
        return;
      }
      try {
        const axiosInstance = createAuthedAxios();
        const { data } = await axiosInstance.get(`${baseURL}/ai-packs/jobs/${job.jobId}`);
        setJob(data.job);
      } catch { /* transient — keep polling */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [job, t]);

  const uploadImage = async (file) => {
    if (!file) return;
    setBusy(true); setError(''); setJob(null); pollCount.current = 0;
    try {
      const axiosInstance = createAuthedAxios();
      const form = new FormData();
      form.append('image', file);
      const { data } = await axiosInstance.post(`${baseURL}/ai-packs/pharmacy/prescription-ocr`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJob(data.job);
    } catch (err) {
      const msg = err?.response?.data?.message || t('aiPacks.upload_failed', 'Could not read the prescription');
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const result = job?.status === 'done' ? job.result : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="prescription-ocr-panel">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full flex flex-col max-h-[85vh]">
        <div className="border-b border-gray-100 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center">
              <Sparkles size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">{t('aiPacks.rx_title', 'Read prescription')}</h2>
              <p className="text-[11px] text-gray-400">{t('aiPacks.rx_sub', 'AI reads the image — you verify every line against the paper')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors"><X size={20} /></button>
        </div>

        <div className="p-4 overflow-y-auto">
          {!job && (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="w-full border-2 border-dashed border-gray-200 hover:border-[#4ABDBD] rounded-2xl p-10 flex flex-col items-center gap-3 text-gray-400 hover:text-[#2D8A8A] transition-colors cursor-pointer disabled:opacity-50"
              data-testid="prescription-ocr-upload"
            >
              {busy
                ? <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-9 h-9 animate-spin" />
                : <Upload size={32} />}
              <span className="text-sm font-medium">{t('aiPacks.rx_upload', 'Upload a photo of the prescription')}</span>
              <span className="text-[11px]">{t('aiPacks.rx_formats', 'JPG / PNG, up to 25 MB')}</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => uploadImage(e.target.files?.[0])} />

          {job && !['done', 'failed'].includes(job.status) && (
            <div className="py-10 flex flex-col items-center gap-3">
              <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin" />
              <p className="text-sm text-gray-500">
                {job.status === 'queued'
                  ? t('aiPacks.rx_reading', 'Reading the image…')
                  : t('aiPacks.rx_structuring', 'Understanding the prescription…')}
              </p>
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="py-8 text-center">
              <AlertCircle size={32} className="mx-auto text-rose-400 mb-2" />
              <p className="text-sm text-gray-600">{t('aiPacks.rx_failed', 'Could not read this image.')}</p>
              <button onClick={() => { setJob(null); setError(''); }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2D8A8A] hover:underline cursor-pointer">
                <RefreshCw size={14} /> {t('aiPacks.try_again', 'Try another photo')}
              </button>
            </div>
          )}

          {result && (
            <>
              {(result.patientName || result.doctorName || result.date) && (
                <p className="text-xs text-gray-500 mb-3">
                  {[result.patientName, result.doctorName, result.date].filter(Boolean).join(' · ')}
                </p>
              )}
              {result.medicines?.length ? (
                <div className="border border-gray-100 rounded-xl divide-y divide-gray-50">
                  {result.medicines.map((m, i) => (
                    <div key={i} className="px-3.5 py-2.5">
                      <p className="text-sm font-semibold text-gray-800">
                        {m.name}{m.strength ? ` ${m.strength}` : ''}{m.form ? ` · ${m.form}` : ''}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        {[m.dosage, m.frequency, m.duration, m.qty ? `qty ${m.qty}` : null].filter(Boolean).join(' · ') || t('aiPacks.rx_no_detail', 'no dosage details read')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">{t('aiPacks.rx_none', 'No medicines could be read from this image.')}</p>
              )}
              <p className="mt-3 text-[11px] text-amber-600 flex items-start gap-1.5">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                {t('aiPacks.rx_verify', 'AI reading — verify every line against the paper prescription before dispensing.')}
              </p>
            </>
          )}

          {error && <p className="mt-3 text-sm text-rose-500">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionOcrPanel;
